import { useState } from "react";
import { Settings, ChevronDown, ChevronUp, Check, Circle } from "lucide-react";
import { useGovernance } from "../../contexts/GovernanceContext";
import { getRequirementLabel } from "../../lib/governanceProfiles";
import { cn } from "../../lib/utils";

const BoardSettingsPanel = ({ className }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { profile, profileId, setProfileId, allProfiles, isSyncing } =
    useGovernance();

  const handleProfileChange = (newProfileId) => {
    setProfileId(newProfileId);
  };

  const getIndicatorColor = (level) => {
    switch (level) {
      case "mandatory":
        return "bg-primary";
      case "strongly-recommended":
        return "bg-primary/80";
      case "recommended":
        return "bg-primary/60";
      case "optional":
        return "bg-muted-foreground/40";
      case "disabled":
        return "bg-destructive/40";
      case "hidden":
        return "bg-muted-foreground/20";
      default:
        return "bg-muted-foreground/40";
    }
  };

  return (
    <div
      className={cn(
        "bg-card/50 backdrop-blur-sm rounded-lg border border-border/50 overflow-hidden",
        className
      )}
    >
      {/* Collapsed header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-muted/50">
            <Settings
              className={cn(
                "w-4 h-4 text-muted-foreground",
                isSyncing && "animate-spin"
              )}
            />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-medium text-foreground">Settings</h3>
            <p className="text-xs text-muted-foreground">{profile.name}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-border/30">
          <div className="pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Governance Profile
            </h4>
            <p className="text-xs text-muted-foreground mb-4">
              Select the profile that matches your organisation
            </p>

            <div className="space-y-2">
              {allProfiles.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleProfileChange(p.id)}
                  className={cn(
                    "w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left",
                    profileId === p.id
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-primary/30 hover:bg-muted/30"
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      profileId === p.id
                        ? "border-primary bg-primary"
                        : "border-muted-foreground/40"
                    )}
                  >
                    {profileId === p.id && (
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {p.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {p.description}
                    </div>

                    {/* Key behaviours shown when selected */}
                    {profileId === p.id && (
                      <div className="mt-3 pt-3 border-t border-border/30 space-y-1.5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              getIndicatorColor(p.governancePulse)
                            )}
                          />
                          <span>
                            Governance review:{" "}
                            {getRequirementLabel(p.governancePulse)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              getIndicatorColor(p.boardChemistry)
                            )}
                          />
                          <span>
                            Board chemistry:{" "}
                            {getRequirementLabel(p.boardChemistry)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              getIndicatorColor(p.decisionOwner)
                            )}
                          />
                          <span>
                            Decision owner:{" "}
                            {getRequirementLabel(p.decisionOwner)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              getIndicatorColor(p.voteRecord)
                            )}
                          />
                          <span>
                            Vote records: {getRequirementLabel(p.voteRecord)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              getIndicatorColor(p.boardTrainingLog)
                            )}
                          />
                          <span>
                            Training log:{" "}
                            {getRequirementLabel(p.boardTrainingLog)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Circle className="w-1.5 h-1.5 text-primary fill-primary" />
                          <span>
                            Uses &ldquo;{p.terminology.decision}&rdquo;
                            terminology
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardSettingsPanel;
